/**
 * TimelineSelector.ts
 * Manages the timeline range selector with draggable handles and snap points
 *
 * Features:
 * - Drag-and-drop handles for range selection
 * - Automatic snapping to valid years (including even years between odd years)
 * - localStorage integration for persistence
 * - Custom event dispatching on range updates
 * - Cross-tab synchronization via storage events
 */

import type { TimelineRange, SnapPoint } from '../types';
import { querySelector, addEventListener } from '../ui/DOMHelpers';
import { StorageService } from '../services/StorageService';
import { TIMELINE_YEARS, EVENTS } from '../utils/constants';

/**
 * Timeline selector component with drag-and-drop functionality
 *
 * The timeline uses a continuous track with discrete snap points for each year.
 * Odd years (99, 01, 03...) are predefined in TIMELINE_YEARS. Even years are
 * interpolated at midpoints between adjacent odd years.
 *
 * Positioning Logic:
 * - Each year maps to a position: `yearIndex / (totalYears - 1)`
 * - Even years interpolate: `(prevPosition + nextPosition) / 2`
 * - Handles snap to nearest year when dragging stops
 *
 * @example
 * ```ts
 * const timeline = new TimelineSelector(storageService);
 * timeline.initialize();
 * timeline.setRange('19', '25');
 * const range = timeline.getRange(); // { leftYear: '19', rightYear: '25' }
 * ```
 */
export class TimelineSelector {
  private track: HTMLElement | null;
  private selection: HTMLElement | null;
  private leftHandle: HTMLElement | null;
  private rightHandle: HTMLElement | null;
  private updateButton: HTMLElement | null;

  private readonly years: readonly string[];
  private isDragging: boolean = false;
  private currentHandle: HTMLElement | null = null;
  private startX: number = 0;
  private startLeft: number = 0;
  private startRight: number = 0;

  private storageService: StorageService;

  // Bound event handlers for proper cleanup
  private boundOnDrag: (e: MouseEvent) => void;
  private boundStopDragging: (e: MouseEvent) => void;

  constructor(storageService: StorageService) {
    this.storageService = storageService;
    this.years = TIMELINE_YEARS;

    // Initialize DOM element references to null
    this.track = null;
    this.selection = null;
    this.leftHandle = null;
    this.rightHandle = null;
    this.updateButton = null;

    // Bind event handlers
    this.boundOnDrag = this.onDrag.bind(this);
    this.boundStopDragging = this.stopDragging.bind(this);
  }

  /**
   * Initialize the timeline selector
   * Must be called after the timeline DOM elements have been rendered
   */
  public initialize(): void {
    // Query DOM elements now that they exist
    this.track = querySelector<HTMLElement>('.timeline-track');
    this.selection = querySelector<HTMLElement>('.selection-area');
    this.leftHandle = querySelector<HTMLElement>('.handle.left');
    this.rightHandle = querySelector<HTMLElement>('.handle.right');
    this.updateButton = querySelector<HTMLElement>('.update-range-button');

    if (!this.track || !this.selection || !this.leftHandle || !this.rightHandle) {
      console.warn('[TimelineSelector] Timeline elements not found in DOM');
      return;
    }

    this.initializeTimeline();
    this.bindEvents();
  }

  /**
   * Set the timeline range programmatically
   */
  public setRange(leftYear: string, rightYear: string): void {
    this.setSelectionPosition(leftYear, rightYear);
  }

  /**
   * Get the current timeline range
   */
  public getRange(): TimelineRange {
    return this.getSelectedYears();
  }

  /**
   * Position the selection based on years
   *
   * Handles both odd years (from TIMELINE_YEARS) and even years (interpolated).
   *
   * Year Position Calculation:
   * - Odd years: Direct lookup in TIMELINE_YEARS array
   *   → position = yearIndex / (totalYears - 1)
   * - Even years: Interpolate between adjacent odd years
   *   → position = (prevPosition + nextPosition) / 2
   *
   * @param leftYear - Left boundary year (e.g., '19')
   * @param rightYear - Right boundary year (e.g., '25')
   *
   * @example
   * ```ts
   * setSelectionPosition('19', '25'); // Sets range to 2019-2025
   * setSelectionPosition('20', '24'); // Sets range to 2020-2024 (even years interpolated)
   * ```
   */
  private setSelectionPosition(leftYear: string, rightYear: string): void {
    if (!this.track || !this.selection || !this.leftHandle || !this.rightHandle) {
      return;
    }

    const getYearPosition = (year: string): number => {
      const yearIndex = this.years.indexOf(year);

      if (yearIndex >= 0) {
        // Year exists in the array (odd year) - use exact position
        return yearIndex / (this.years.length - 1);
      } else {
        // Even year - interpolate position between surrounding odd years
        const yearNum = parseInt(year, 10);
        const prevYear = String(yearNum - 1).padStart(2, '0');
        const nextYear = String(yearNum + 1).padStart(2, '0');

        const prevIndex = this.years.indexOf(prevYear);
        const nextIndex = this.years.indexOf(nextYear);

        if (prevIndex >= 0 && nextIndex >= 0) {
          const prevPosition = prevIndex / (this.years.length - 1);
          const nextPosition = nextIndex / (this.years.length - 1);
          return (prevPosition + nextPosition) / 2;
        } else if (prevIndex >= 0) {
          const prevPosition = prevIndex / (this.years.length - 1);
          return prevPosition + 1 / (this.years.length - 1) / 2;
        } else if (nextIndex >= 0) {
          const nextPosition = nextIndex / (this.years.length - 1);
          return nextPosition - 1 / (this.years.length - 1) / 2;
        }
      }

      return 0; // fallback
    };

    const leftPosition = getYearPosition(leftYear);
    const rightPosition = getYearPosition(rightYear);

    if (leftPosition >= 0 && rightPosition >= 0) {
      const trackWidth = this.track.offsetWidth;
      const leftPos = leftPosition * trackWidth;
      const rightPos = trackWidth - rightPosition * trackWidth;

      this.selection.style.left = `${leftPos}px`;
      this.selection.style.right = `${rightPos}px`;

      this.leftHandle.setAttribute('data-year', `'${leftYear}`);
      this.rightHandle.setAttribute('data-year', `'${rightYear}`);
    }
  }

  /**
   * Get the currently selected years from handle data attributes
   */
  private getSelectedYears(): TimelineRange {
    const leftYear = this.leftHandle?.getAttribute('data-year')?.substring(1) || '19';
    const rightYear = this.rightHandle?.getAttribute('data-year')?.substring(1) || '25';

    return { leftYear, rightYear };
  }

  /**
   * Calculate snap points for all years (including even years between odd years)
   *
   * Algorithm:
   * 1. For each odd year in TIMELINE_YEARS: create snap point at exact position
   * 2. Between each pair of odd years: create snap point for even year at midpoint
   * 3. Position formula: (yearIndex / totalOddYears) × trackWidth
   *
   * Example (simplified):
   * - Odd years: ['99', '01', '03'] → positions at [0%, 50%, 100%]
   * - Even year '00' → position at 25% (midpoint of '99' and '01')
   * - Even year '02' → position at 75% (midpoint of '01' and '03')
   *
   * @returns Array of snap points with year and pixel position
   */
  private calculateSnapPoints(): SnapPoint[] {
    if (!this.track) return [];

    const trackWidth = this.track.offsetWidth;
    const totalYears = this.years.length - 1;
    const points: SnapPoint[] = [];

    this.years.forEach((year, index) => {
      const position = (index / totalYears) * trackWidth;
      points.push({ position, year });

      // Add midpoint for even years
      if (index < totalYears) {
        const nextPosition = ((index + 1) / totalYears) * trackWidth;
        const midPosition = (position + nextPosition) / 2;
        const midYear = String(parseInt(year, 10) + 1).padStart(2, '0');
        points.push({ position: midPosition, year: midYear });
      }
    });

    return points;
  }

  /**
   * Find the nearest snap point to a given position
   */
  private findNearestSnapPoint(position: number): SnapPoint | null {
    const snapPoints = this.calculateSnapPoints();
    const snapThreshold = 15;

    let nearest: SnapPoint | null = null;
    let minDistance = Infinity;

    snapPoints.forEach(point => {
      const distance = Math.abs(point.position - position);
      if (distance < minDistance && distance < snapThreshold) {
        minDistance = distance;
        nearest = point;
      }
    });

    return nearest;
  }

  /**
   * Start dragging a handle
   */
  private startDragging(e: MouseEvent, handle: HTMLElement): void {
    if (!this.track || !this.selection) return;

    this.isDragging = true;
    this.currentHandle = handle;
    this.startX = e.pageX;

    const rect = this.selection.getBoundingClientRect();
    const trackRect = this.track.getBoundingClientRect();

    this.startLeft = rect.left - trackRect.left;
    this.startRight = trackRect.right - rect.right;

    document.addEventListener('mousemove', this.boundOnDrag);
    document.addEventListener('mouseup', this.boundStopDragging);
  }

  /**
   * Handle dragging motion
   */
  private onDrag(e: MouseEvent): void {
    if (!this.isDragging || !this.track || !this.selection || !this.currentHandle) {
      return;
    }

    const delta = e.pageX - this.startX;
    const trackRect = this.track.getBoundingClientRect();

    if (this.currentHandle === this.leftHandle) {
      const newLeft = Math.max(
        0,
        Math.min(this.startLeft + delta, trackRect.width - this.startRight - 20)
      );
      const snapPoint = this.findNearestSnapPoint(newLeft);

      if (snapPoint) {
        this.selection.style.left = `${snapPoint.position}px`;
        this.currentHandle.setAttribute('data-year', `'${snapPoint.year}`);
      } else {
        this.selection.style.left = `${newLeft}px`;
      }
    } else {
      const newRight = Math.max(
        0,
        Math.min(this.startRight - delta, trackRect.width - this.startLeft - 20)
      );
      const snapPoint = this.findNearestSnapPoint(trackRect.width - newRight);

      if (snapPoint) {
        this.selection.style.right = `${trackRect.width - snapPoint.position}px`;
        this.currentHandle.setAttribute('data-year', `'${snapPoint.year}`);
      } else {
        this.selection.style.right = `${newRight}px`;
      }
    }
  }

  /**
   * Stop dragging and snap to nearest point
   */
  private stopDragging(): void {
    if (!this.track || !this.selection) return;

    this.isDragging = false;
    document.removeEventListener('mousemove', this.boundOnDrag);
    document.removeEventListener('mouseup', this.boundStopDragging);

    // Ensure handle snaps to nearest point when released
    if (this.currentHandle === this.leftHandle) {
      const rect = this.selection.getBoundingClientRect();
      const trackRect = this.track.getBoundingClientRect();
      const left = rect.left - trackRect.left;
      const snapPoint = this.findNearestSnapPoint(left);

      if (snapPoint && this.currentHandle) {
        this.selection.style.left = `${snapPoint.position}px`;
        this.currentHandle.setAttribute('data-year', `'${snapPoint.year}`);
      }
    } else if (this.currentHandle === this.rightHandle) {
      const rect = this.selection.getBoundingClientRect();
      const trackRect = this.track.getBoundingClientRect();
      const right = trackRect.right - rect.right;
      const snapPoint = this.findNearestSnapPoint(trackRect.width - right);

      if (snapPoint && this.currentHandle) {
        this.selection.style.right = `${trackRect.width - snapPoint.position}px`;
        this.currentHandle.setAttribute('data-year', `'${snapPoint.year}`);
      }
    }

    this.currentHandle = null;
  }

  /**
   * Initialize timeline position from storage
   */
  private initializeTimeline(): void {
    let leftYear: string = '19';
    let rightYear: string = '25';

    const stored = this.storageService.getTimelineRange();
    if (stored) {
      leftYear = stored.leftYear;
      rightYear = stored.rightYear;
    }

    this.setSelectionPosition(leftYear, rightYear);

    // Refresh after a short delay to ensure we have latest data
    setTimeout(() => {
      this.refreshFromStorage();
    }, 100);
  }

  /**
   * Bind event listeners
   */
  private bindEvents(): void {
    if (!this.leftHandle || !this.rightHandle) return;

    // Handle dragging
    addEventListener(this.leftHandle, 'mousedown', (e) => {
      if (this.leftHandle) {
        this.startDragging(e, this.leftHandle);
      }
    });

    addEventListener(this.rightHandle, 'mousedown', (e) => {
      if (this.rightHandle) {
        this.startDragging(e, this.rightHandle);
      }
    });

    // Update button
    if (this.updateButton) {
      addEventListener(this.updateButton, 'click', () => {
        const { leftYear, rightYear } = this.getSelectedYears();
        this.storageService.setTimelineRange({ leftYear, rightYear });

        // Dispatch timeline change event to trigger content update without page reload
        document.dispatchEvent(new CustomEvent(EVENTS.TIMELINE_CHANGED));
      });
    }

    // Listen for localStorage changes from other tabs
    window.addEventListener('storage', (e) => {
      if (e.key === 'timelineRange') {
        this.refreshFromStorage();
      }
    });

    // Listen for page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.refreshFromStorage();
      }
    });
  }

  /**
   * Refresh timeline from storage (for cross-tab synchronization)
   */
  private refreshFromStorage(): void {
    const stored = this.storageService.getTimelineRange();
    if (stored) {
      this.setSelectionPosition(stored.leftYear, stored.rightYear);
    }
  }
}
